import { STATES_COLORS, StaticDataType, STATIC_DATA_STATES } from '../charts/chart-components'
import { RawStaticDataEvent, StaticDataEvent, StaticDataSeries, TimeSpan, ValueUom, xRangeEvent } from '../utils/type'
import { EMPTY_DATA } from '../utils/mock'

export const enum STATIC_DATA_ENTITIES {
    RIG = 'rig',
    WELL = 'well',
    INTERVENTION = 'intervention',
    SECTION = 'section',
    RUN = 'run',
    TUBULAR_TYPE = 'tubularType',
    RIG_TABLE = 'rigTable'
}

const mapRawEventsToSeriesData = (
    events: RawStaticDataEvent[],
    chartTimeSpan: TimeSpan,
    parentSeries: StaticDataSeries
): StaticDataEvent[] => {
    let emptyIntervals: StaticDataEvent[] = []
    if (!events?.length) return emptyIntervals

    if (events.length > 1) {
        events.forEach((event, i) => {
            if (event?.end && events[i + 1]?.start && event.end < events[i + 1].start) {
                emptyIntervals = [...emptyIntervals, ...getEmptyData(event.end, events[i + 1].start, parentSeries)]
            }
        })
    }

    return [
        ...emptyIntervals,
        ...events?.map((event: RawStaticDataEvent) => getXrangeEvent(event, parentSeries, chartTimeSpan))
    ]
}

const getXrangeEvent = (
    event: RawStaticDataEvent,
    parentSeries: StaticDataSeries,
    chartTimeSpan: TimeSpan
): xRangeEvent => {
    let end = event.end
    const parentData = parentSeries?.data?.findLast(p => p?.x && event?.start && p.x <= event.start) || null

    // if the event doesn't have end, try to get parent end
    if (!end) end = parentData?.x2
    if (!end) end = chartTimeSpan?.end

    return {
        name: event?.name,
        color: event.end
            ? STATES_COLORS[STATIC_DATA_STATES.FINISHED]
            : event.id
            ? STATES_COLORS[STATIC_DATA_STATES.STARTED_IN_EXECUTION]
            : STATES_COLORS[STATIC_DATA_STATES.EMPTY],
        // if for some reason the event doesn't have a start, the track will have zero length
        x: event?.start || end,
        x2: end,
        y: 0,
        isRealtime: !!parentData?.isRealtime,
        rawEvent: {
            ...event,
            state: !event?.end ? STATIC_DATA_STATES.STARTED_IN_EXECUTION : STATIC_DATA_STATES.FINISHED
        }
    }
}

export const getXRangeSeries = (
    data: Array<RawStaticDataEvent>,
    chartTimeSpan: TimeSpan,
    parentSeries: StaticDataSeries,
    type: StaticDataType
): StaticDataSeries => {
    const seriesData = mapRawEventsToSeriesData(data, chartTimeSpan, parentSeries)

    let xRangeSeriesData: Array<xRangeEvent> =
        seriesData && seriesData.length
            ? [
                  ...getEmptyData(chartTimeSpan.begin, seriesData[0]?.x, parentSeries),
                  ...seriesData,
                  ...getEmptyData(seriesData[seriesData.length - 1]?.x2, chartTimeSpan.end, parentSeries)
              ]
            : [...getEmptyData(chartTimeSpan.begin, chartTimeSpan.end, parentSeries)]
    return {
        name: type,
        type: 'xrange',
        data: xRangeSeriesData
    }
}

export const sortFetchedStaticData = obj => {
    if (!obj) return null
    Object.entries(obj).forEach(x => {
        if (x[1] instanceof Array) {
            x[1].sort((a, b) => {
                if (a.start && b.start) return a.start > b.start ? 1 : -1

                return -1
            })
        }
    })

    return obj
}

export const getEmptyData = (start?: number, end?: number, parentSeries?: StaticDataSeries): xRangeEvent[] => {
    if (!start || !end || !parentSeries || start >= end) return []

    const parentDataInsideInterval =
        parentSeries.data?.filter(p => (start <= p.x && p.x <= end) || (start <= p.x2 && p.x2 <= end)) ?? []

    if (!parentDataInsideInterval.length) return EMPTY_DATA(start, end, STATIC_DATA_STATES.EMPTY_DISABLED, null)

    const result: xRangeEvent[] = []

    parentDataInsideInterval.forEach(parentData => {
        if (!parentData?.rawEvent?.start) return
        const d = EMPTY_DATA(
            parentData.x < start ? start : parentData.x,
            parentData?.x2 ?? end,
            parentData?.rawEvent?.state === STATIC_DATA_STATES.EMPTY ||
                parentData?.rawEvent?.state === STATIC_DATA_STATES.EMPTY_DISABLED ||
                parentData?.rawEvent?.state === STATIC_DATA_STATES.FINISHED
                ? STATIC_DATA_STATES.EMPTY_DISABLED
                : STATIC_DATA_STATES.EMPTY,
            parentData
        )?.[0]
        if (d) result.push(d)
    })

    return result
}

export function getFormattedGoalLabel(goal) {
    if (!goal?.attributes) return ''
    let formattedLabels = goal.attributes.map((attr, idx) => {
        if (attr.acronym === 'NA' || attr.acronym === 'UN') return ''

        // macro
        if (idx === 0) return ` - ${attr.title} `

        if (idx === 1 || idx === 2 || idx === 5 || idx === 6 || idx === 8 || idx === 9) return `- ${attr.title} `

        // stand size
        if (idx === 3) {
            const explodedWords = attr.title.split(' ')
            return `- ${explodedWords[0]} `
        }

        // tubular/section OD
        if (idx === 4) {
            if (!attr?.inputs?.tubularInputValues) return ''
            const min = attr.inputs.tubularInputValues.find(i => i.key === 'Y')
            const max = attr.inputs.tubularInputValues.find(i => i.key === 'X')
            if (!isNaN(min.value) && !isNaN(max.value)) return `- >=${min.value}to${max.value}<= `
            if (!isNaN(min.value)) return `- >=${min.value} `
            if (!isNaN(min.value) && !isNaN(max.value)) return `- ${max.value}<= `
        }

        // restricted performance
        if (idx === 7) {
            if (attr.acronym === 'RXyyy') {
                return '- Performance Multiple Options '
            } else {
                return `- Performance ${attr.title} `
            }
        }
    })

    const macro = formattedLabels[0]
    const tubularActivites = formattedLabels[1]
    const kpi = formattedLabels[2]
    const standSize = formattedLabels[3]
    const OD = formattedLabels[4]
    const movementDirection = formattedLabels[5]
    const sectionHoleStatus = formattedLabels[6]
    const restrictedPerformance = formattedLabels[7]
    const materialType = formattedLabels[8]
    const connectionType = formattedLabels[9]

    return (
        macro +
        kpi +
        restrictedPerformance +
        standSize +
        movementDirection +
        OD +
        materialType +
        connectionType +
        sectionHoleStatus
    )
}

export const generateRestrictedGoalValue = (restrictedValueUom: ValueUom, restrictedAcronym?: string) => {
    let restrictedGoalValue = {
        label: '' as string,
        value: '' as ValueUom | string
    }

    if (restrictedValueUom?.value) {
        restrictedGoalValue = {
            label: `${restrictedValueUom.value} ${restrictedValueUom?.uom?.name ??
                restrictedValueUom?.name ??
                ''}${restrictedAcronym ??
                restrictedValueUom.speedAcronym ??
                restrictedValueUom.timeConnectionAcronym ??
                restrictedValueUom.ropSpeed ??
                ''}`,
            value: restrictedValueUom
        }
    }

    return restrictedGoalValue
}
